import React, { useState, useEffect } from "react";
import { Drawer, Badge, Empty, Typography, Button, Spin } from "antd";
import { ShoppingOutlined, ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getProductoPorId } from "../../utils/productos/productoUtils";
import { getServicioPorId } from "../../utils/servicios/serviciosUtils";
import CartItem from "../../components/CartItem";
import { marcarDisponibilidad } from "../../utils/carrito/carritoUtils";
import "./MobileCartSheet.scss";

const { Text, Title } = Typography;

const MobileCartSheet = ({ visible, onClose, cartItems }) => {
  const navigate = useNavigate();
  const [productoDetalles, setProductoDetalles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const obtenerDetallesTodos = async () => {
      if (!cartItems || (!cartItems.productos?.length && !cartItems.servicios?.length)) {
        setProductoDetalles([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const promisesProductos = cartItems.productos?.map(async (p) => {
          const detalle = await getProductoPorId(p.id);
          return { ...detalle, tipo: "producto", cantidad: p.cantidad };
        }) || [];

        const promisesServicios = cartItems.servicios?.map(async (s) => {
          const detalle = await getServicioPorId(s.id);
          return {
            ...detalle,
            tipo: "servicio",
            cantidadDuracion: s.cantidadDuracion,
            fecha: s.fecha,
            hora: s.hora,
          };
        }) || [];

        const resultados = await Promise.all([
          ...(promisesProductos || []),
          ...(promisesServicios || []),
        ]);
        setProductoDetalles(await marcarDisponibilidad(resultados.filter((item) => item !== null)));
      } catch (error) {
        console.error("Error al cargar carrito:", error);
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      obtenerDetallesTodos();
    }
  }, [visible, cartItems]);

  const getTotalPrice = (items) => {
    return items.reduce((total, item) => {
      let subtotal = 0;
      if (item.tipo === "producto") {
        subtotal = item.total * item.cantidad;
      } else {
        subtotal =
          item.duracion === "unico"
            ? item.total
            : item.total * item.cantidadDuracion;
      }
      return total + subtotal;
    }, 0);
  };

  const disponibles = productoDetalles.filter((item) => !item.noDisponible);
  const hayNoDisponibles = disponibles.length !== productoDetalles.length;
  const total = disponibles.length > 0 ? getTotalPrice(disponibles) : 0;

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  const handleContinueShopping = () => {
    onClose();
    navigate("/");
  };

  const hasItems = productoDetalles.length > 0;
  const totalItems = (cartItems?.productos?.length || 0) + (cartItems?.servicios?.length || 0);

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="bottom"
      closable={false}
      height="85vh"
      className="mobile-cart-sheet"
      styles={{
        body: { padding: 0 },
        header: { display: "none" },
      }}
    >
      <div className="cart-sheet-header">
        <div className="header-left">
          <ShoppingOutlined />
          <Title level={4}>Mi carrito</Title>
          {totalItems > 0 && (
            <Badge count={totalItems} className="cart-badge" />
          )}
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          className="close-btn"
        />
      </div>

      {loading ? (
        <div className="cart-loading">
          <Spin size="large" />
          <Text>Cargando tu carrito...</Text>
        </div>
      ) : !hasItems ? (
        <div className="cart-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className="empty-content">
                <Text strong style={{ fontSize: "16px", color: "#374151" }}>
                  Tu carrito está vacío
                </Text>
                <Text type="secondary" style={{ fontSize: "14px" }}>
                  Explora productos y servicios increíbles
                </Text>
              </div>
            }
          >
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={handleContinueShopping}
              className="discover-btn"
            >
              Descubrir productos
            </Button>
          </Empty>
        </div>
      ) : (
        <div className="cart-sheet-content">
          <div className="cart-items-scroll">
            {productoDetalles.map((item) => (
              <CartItem key={`${item.id}-${item.tipo}`} item={item} />
            ))}
          </div>

          <div className="cart-sheet-footer">
            <div className="summary-box">
              <div className="summary-row">
                <Text type="secondary">Subtotal ({totalItems} {totalItems === 1 ? "producto" : "productos"})</Text>
                <Text strong className="total-amount">${total.toFixed(2)} MXN</Text>
              </div>
            </div>

            <Button
              type="primary"
              block
              size="large"
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={hayNoDisponibles || disponibles.length === 0}
            >
              Ir a pagar
            </Button>

            <Button
              type="text"
              block
              className="continue-btn"
              onClick={handleContinueShopping}
            >
              Continuar comprando
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default MobileCartSheet;